import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadTab = ({ onFileSelect, isProcessing }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const supportedFormats = [
    { extension: '.csv', description: 'Comma-separated values' },
    { extension: '.txt', description: 'Plain text file' }
  ];

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    const validTypes = ['text/csv', 'text/plain', 'application/csv'];
    const validExtensions = ['.csv', '.txt'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      alert('Please select a CSV or TXT file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      alert('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* File Upload Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
          isDragOver
            ? 'border-primary bg-primary/5'
            : selectedFile
            ? 'border-success bg-success/5' :'border-border hover:border-primary/50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={isProcessing}
        />

        {selectedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-success/10 rounded-full">
              <Icon name="FileCheck" size={32} className="text-success" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-text-primary">File Selected</h3>
              <p className="text-sm text-text-secondary mt-1">{selectedFile.name}</p>
              <p className="text-xs text-text-secondary">{formatFileSize(selectedFile.size)}</p>
            </div>
            <div className="flex justify-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveFile}
                iconName="X"
                iconPosition="left"
                disabled={isProcessing}
              >
                Remove
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBrowseClick}
                iconName="Upload"
                iconPosition="left"
                disabled={isProcessing}
              >
                Choose Different File
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full">
              <Icon name="Upload" size={32} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-text-primary">
                Drag and drop your file here
              </h3>
              <p className="text-sm text-text-secondary mt-1">
                or click to browse your computer
              </p>
            </div>
            <Button
              variant="primary"
              onClick={handleBrowseClick}
              iconName="FolderOpen"
              iconPosition="left"
              disabled={isProcessing}
            >
              Browse Files
            </Button>
          </div>
        )}
      </div>

      {/* File Requirements */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center">
          <Icon name="Info" size={16} className="mr-2 text-accent" />
          File Requirements
        </h4>
        <div className="space-y-3">
          <div>
            <h5 className="text-xs font-medium text-text-primary mb-2">Supported Formats:</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {supportedFormats.map((format) => (
                <div key={format.extension} className="flex items-center space-x-2 text-xs">
                  <Icon name="FileText" size={14} className="text-text-secondary" />
                  <span className="font-medium text-text-primary">{format.extension}</span>
                  <span className="text-text-secondary">- {format.description}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border-light">
            <div>
              <h5 className="text-xs font-medium text-text-primary mb-1">File Size Limit:</h5>
              <p className="text-xs text-text-secondary">Maximum 10MB per file</p>
            </div>
            <div>
              <h5 className="text-xs font-medium text-text-primary mb-1">Email Format:</h5>
              <p className="text-xs text-text-secondary">One email per line or comma-separated</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sample Format */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center">
          <Icon name="Eye" size={16} className="mr-2 text-accent" />
          Sample File Format
        </h4>
        <div className="bg-gray-50 rounded-md p-3 text-xs font-mono">
          <div className="text-text-secondary mb-2"># CSV Format</div>
          <div className="space-y-1">
            <div>john.doe@example.com</div>
            <div>jane.smith@company.org</div>
            <div>contact@business.net</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadTab;